/**
 * This component provides a functionality to user to update or add new E-Mail address into My Account
 */
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { IBreadcrumb } from '../../../core/breadcrumb/breadcrumbs.model';
import { BreadcrumbsService } from '../../../core/breadcrumb/breadcrumbs.service';
import { ConfirmMatchValidator } from '../validators/confirm-match-validator';
import { EmailTextPromoService } from '../../../email-text-promo/email-text-promo.service';
import { EmailValidator } from '../validators/email-validator';
import { UpdateProfileService } from '../update-profile.service';
import { UpdateElectronicCommunicationInfo } from '../models/update-profile-model';
import { UserSession } from '@mem-shared/user-session';
import { GoogleAnalyticsService } from '../../../core/google-analytics/google-analytics.service';
import { ModalDirective } from 'ngx-bootstrap';

@Component({
  selector: 'mem-update-email',
  templateUrl: './update-email.component.html',
  styleUrls: ['./update-email.component.scss']
})
export class UpdateEmailComponent implements OnInit {
  // to show success email modal
  @ViewChild('emailSuccessModal') emailSuccessModel: ModalDirective;
  // to check duplicate email
  duplicateEmail: Boolean = false;
  // to check form is submitted or not
  submitted: boolean;
  // to take form input from HTML
  updateEmailForm: FormGroup;
  // to get existing e-mail addresses
  primaryEmailAddress: string;
  secondaryEmailAdddress: string;
  // These are used to send post request in desired format
  responseRecordType: string[];
  updateElectronicCommunicationInfoo: UpdateElectronicCommunicationInfo = Object();
  // show service error scenario
  showServiceError: boolean;
  showDeleteVerbiage: boolean;
  // check MFA verification
  showMultiFactor: Boolean;

  /**
   * Getter for form field newEmail
   */
  get newEmail() {
    return this.updateEmailForm.get('newEmail');
  }

  /**
   * Getter for form field confirmEmail
   */
  get confirmEmail() {
    return this.updateEmailForm.get('confirmEmail');
  }

  constructor(
    private fb: FormBuilder,
    private uploadProfileService: UpdateProfileService,
    private emailTextPromoService: EmailTextPromoService,
    public userSession: UserSession,
    public router: Router,
    private ga: GoogleAnalyticsService,
    public breadcrumbsService: BreadcrumbsService
  ) { }
  /**
   * This is called when this component is loaded 
   * */
  ngOnInit() {
    this.showMultiFactor = (this.userSession.getMemberFeatureFlags().showMultiFactorAuthConsent == true) && (this.userSession.getMemberFeatureFlags().contactEmailConfirmed || this.userSession.getMemberFeatureFlags().contactPhoneConfirmed);
    this.setBreadcrumbs();
    if (this.router.url.endsWith('deleteEmail')) {
      this.showDeleteVerbiage = true;
    }
    this.updatingEmailForm();
    this.fetchExistingEmail();

    //GA: Record change email update in google analaytics    
    this.CaptureUpdateEmailGA();
  }
  CaptureUpdateEmailGA() {
    if (typeof this.ga.submitEventToGA === "function") {
      this.ga.submitEventToGA('Click : Change Email', 'Change Email');
    }
  }
  /**
   * this function is called when form is submitted on HTML page
   */
  updateEmail() {

    this.submitted = true;
    // Service call is made when valid form is submitted
    if (this.updateEmailForm.valid && !this.duplicateEmail) {
      this.responseRecordType = ['email'];
      this.updateElectronicCommunicationInfoo.emailCommunicationInfo = Object();

      if (this.newEmail && this.newEmail.value) {
        this.updateElectronicCommunicationInfoo.emailCommunicationInfo.newEmailAddress = this.newEmail.value;
      }
      // service call to updateMemberCommunicationInfo to update email info
      this.uploadProfileService
        .updateMemberCommunicationInfo(this.updateElectronicCommunicationInfoo, this.responseRecordType)
        .subscribe(data => {

          // This shows modal pop up when successful response is recieved
          this.emailSuccessModel.show();
        },
        error => {
          this.showServiceError = true;
        });
    }
  }

  /* Create form using form builder and add form controls*/
  private updatingEmailForm() {
    this.updateEmailForm = this.fb.group(
      {
        // Validators.email will check for valid email address. You can also use Validators.pattern();
        // and pass a custom pattern if you need a more comprehensive email check then default angular.
        newEmail: ['', [Validators.required, Validators.maxLength(50), Validators.pattern("^[^@]+@[^@]+\.[^@]+[^@.]+$") ]],
        confirmEmail: ['', [Validators.required, Validators.maxLength(50), Validators.pattern("^[^@]+@[^@]+\.[^@]+[^@.]+$")]]
      },
      { validator: ConfirmMatchValidator.match('confirmEmail', 'newEmail') }
    );
  }

  /**
* Call the email Text promo service to store the values of Existing Phone Number
*/
  fetchExistingEmail() {
    this.emailTextPromoService.getMemberCommunicationInfo().subscribe(
      data => {

        this.primaryEmailAddress = data.communicationAndConsentDetails.emailCommunicationInfo.primaryEmailInfo.emailAddress;
        this.secondaryEmailAdddress = data.communicationAndConsentDetails.emailCommunicationInfo.secondaryEmailInfo.emailAddress;
      }
    );
  }

  /**
* This function will call EmailTextPromoService to check duplicate email
*/
  callDuplicateEmail(newEmail: string) {
    if ((this.primaryEmailAddress.toLowerCase() === newEmail.toLowerCase() || this.secondaryEmailAdddress.toLowerCase() === newEmail.toLowerCase())) {
      this.duplicateEmail = true;
      return true;
    } else {
      this.duplicateEmail = false;
      return false;
    }
  }

  /**
   * This function is used to navigate to the previous page from where it is being called
   */
  goToPreviousPage() {
   
      if(this.userSession.getSession('previousUrl') == "/profile/policyHolder" || this.userSession.getSession('previousUrl') == "/profile/policyHolder/home"){
        this.router.navigate(["/profile/communicationpreference"]);
      }else{
        this.router.navigate([this.userSession.getSession('previousUrl')]);
      }
  }

  /**
   * This function is used to load breadcrumbs for account recovery page based on conditions
   */
  setBreadcrumbs() {
    if (this.showMultiFactor && (this.userSession.getSession('previousUrl') === '/emailTextPromo/home' || this.userSession.getSession('previousUrl') === '/emailTextPromo')) {
      let breadcrumb: IBreadcrumb[] = [{
        label: 'Two step Verification',
        params: {},
        url: '/emailTextPromo/home'
      },
      {
        label: 'Edit Email Address',
        params: {},
        url: ''
      }];
      this.breadcrumbsService.store(breadcrumb);
    }
    if (!this.showMultiFactor && (this.userSession.getSession('previousUrl') === '/emailTextPromo/home' || this.userSession.getSession('previousUrl') === '/emailTextPromo')) {
      let breadcrumb: IBreadcrumb[] = [
        {
          label: 'Account Recovery',
          params: {},
          url: '/emailTextPromo/home'
        },
        {
          label: 'Edit Email Address',
          params: {},
          url: ''
        }];
      this.breadcrumbsService.store(breadcrumb);
    }
  }

}



// WEBPACK FOOTER //
// ./src/app/shared/update-profile/update-email/update-email.component.ts